/**
 * Set up the legal table of contents sidebar
 * Use an intersection observer to watch for headings entering/exiting the top area of viewport
 * Highlight the active heading in the sidebar
 */
function setupLegalTableOfContents() {
    const sidebarToc = document.querySelector('#legal-table-content-cloud');

    if (!sidebarToc) {
        return;
    }

    // Search for headings directly in the document since they might be in nested containers
    const headings = Array.from(document.querySelectorAll('h2[id]'));
    const tocLinks = sidebarToc.querySelectorAll('a');

    if (headings.length === 0 || tocLinks.length === 0) {
        return;
    }

    // create a map of id to toc link for faster lookups
    const idToLinkMap = new Map();
    tocLinks.forEach(link => {
        const href = link.getAttribute('href');
        if (href && href.startsWith('#')) {
            idToLinkMap.set(href.substring(1), link);
        }
    });

    let activeLink = null;
    let userClickedToc = false;

    // Check for hash in URL on page load
    if (window.location.hash) {
        const hash = window.location.hash.substring(1);
        if (idToLinkMap.has(hash)) {
            userClickedToc = true;
            setTimeout(() => {
                updateToc(hash);
                // Reset after a delay to resume normal behavior
                setTimeout(() => {
                    userClickedToc = false;
                }, 500);
            }, 0);
        }
    }

    // update data-active attribute on active toc links
    function updateToc(id) {
        if (!id) {
            return;
        }

        // avoid unnecessary dom operations by tracking the active link
        if (activeLink) {
            activeLink.removeAttribute('data-active');
        }

        // use the map for direct lookup instead of querying the dom
        activeLink = idToLinkMap.get(id);
        if (activeLink) {
            activeLink.setAttribute('data-active', 'true');
        }
    }

    // add click event listener to toc links
    tocLinks.forEach(link => {
        link.addEventListener('click', () => {
            const href = link.getAttribute('href');
            if (href && href.startsWith('#')) {
                userClickedToc = true;
                const id = href.substring(1);
                updateToc(id);

                // Reset the flag after a delay to resume normal behavior
                setTimeout(() => {
                    userClickedToc = false;
                }, 1000);
            }
        });
    });

    // Track which headings are currently intersecting
    const intersectingHeadings = new Map();

    // watch for headings entering/exiting the top area of viewport
    const tocHeadingsObserver = new IntersectionObserver((entries) => {
        // Skip observer updates if user just clicked a TOC link
        if (userClickedToc) {
            return;
        }

        // Update intersecting headings map
        entries.forEach(entry => {
            const id = entry.target.getAttribute('id');
            if (!id || !idToLinkMap.has(id)) {
                return;
            }

            if (entry.isIntersecting) {
                intersectingHeadings.set(id, entry);
            } else {
                intersectingHeadings.delete(id);
            }
        });

        // Find the heading that's closest to the top of the viewport
        if (intersectingHeadings.size === 0) {
            return;
        }

        let currentHeadingId = null;
        let minTop = Infinity;

        intersectingHeadings.forEach((entry, id) => {
            const rect = entry.boundingClientRect;
            const top = rect.top;

            // Prefer headings that are at or above the viewport threshold
            if (top <= 200 && top < minTop) {
                minTop = top;
                currentHeadingId = id;
            }
        });

        // If no heading found above threshold, use the one closest to top
        if (!currentHeadingId) {
            intersectingHeadings.forEach((entry, id) => {
                const rect = entry.boundingClientRect;
                if (rect.top < minTop) {
                    minTop = rect.top;
                    currentHeadingId = id;
                }
            });
        }

        if (currentHeadingId) {
            updateToc(currentHeadingId);
        }
    }, {
        rootMargin: '-100px 0px -80% 0px',
        threshold: [0, 0.1, 0.5, 1]
    });

    headings.forEach(heading => tocHeadingsObserver.observe(heading));

    // Initial check on page load
    setTimeout(() => {
        if (!window.location.hash) {
            // Activate privacy-policy by default when no hash is present
            if (idToLinkMap.has('privacy-policy')) {
                updateToc('privacy-policy');
            }
        }
    }, 100);
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupLegalTableOfContents);
} else {
    setupLegalTableOfContents();
}

